/**
 * The Vibed Boost mark: a rise out of a level meter.
 *
 * ## Why the earned boost gets a mark and the paid tiers do not
 *
 * A paid boost is a transaction and reads perfectly well as the word "boost"
 * next to a price. The earned one is a currency with a rule behind it, held in
 * an account, spent later somewhere else - and a thing you carry around needs
 * to be recognisable at a glance in all the places it turns up. It was a plain
 * arrow in the account menu and nothing at all everywhere else, which is how
 * the same idea ends up looking like four different features.
 *
 * ## One colour, inherited
 *
 * `currentColor` throughout, with the outer bars stepped down by opacity rather
 * than drawn in a second colour. Two-tone would need a lighter green than
 * `--color-fresh` that no theme defines, and the light theme's fresh is dark
 * enough that a hardcoded mint would vanish on white. Inheriting means the mark
 * is fresh where the boost is ready, faint where it is being explained, and
 * correct in both themes without either one being thought about.
 *
 * The arrow keeps full opacity, so it stays the thing the eye lands on at
 * 14px - which is most of the sizes this is drawn at.
 */
export function BoostMark({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 96 96" className={className} fill="currentColor" aria-hidden="true">
      <path d="M48 18 L62 34 L54 34 L54 44 L42 44 L42 34 L34 34 Z" />
      <rect x="24" y="56" width="7" height="22" rx="3.5" opacity="0.55" />
      <rect x="35" y="50" width="7" height="28" rx="3.5" opacity="0.75" />
      <rect x="46" y="42" width="7" height="36" rx="3.5" />
      <rect x="57" y="50" width="7" height="28" rx="3.5" opacity="0.75" />
      <rect x="68" y="56" width="7" height="22" rx="3.5" opacity="0.55" />
    </svg>
  );
}

/**
 * The arrow on its own.
 *
 * For the one place the bars are drawn at full size underneath it - the dialog
 * banner - where the mark would otherwise be sitting on top of a larger copy of
 * its own bottom half.
 *
 * Cropped to the arrow rather than sharing the mark's square, because the
 * banner places it against the top of a bar and two-thirds of that square is
 * the empty space where the bars would have been - which reads as the arrow
 * floating an inch above the thing it is supposed to be rising out of.
 */
export function BoostArrow({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="32 16 32 30" className={className} fill="currentColor" aria-hidden="true">
      <path d="M48 18 L62 34 L54 34 L54 44 L42 44 L42 34 L34 34 Z" />
    </svg>
  );
}
